import { unstable_cache } from "next/cache"
import { z } from "zod"

interface ClampStringProps {
  value: string
  maxLength?: number
  ellipsisString?: string
}
/**
 * It takes a string and returns a string that is at most `maxLength` characters long. If the string is
 * longer than `maxLength`, it is truncated and an ellipsis is appended to the end
 * @param value  - It is a required string that we want to clamp (if necessary)
 * @param maxLength - It is optional and it is the max length that we will return. By default it is 18
 * @param ellipsisString - It is optional. It is the string we will return at the end of the clamped text. By default it is "..."
 * @returns A string.
 */
export function clampString({ value, maxLength = 18, ellipsisString = "..." }: ClampStringProps): string {
  if (!value || !value.trim() || maxLength < 1) return ""

  if (value.length > maxLength) return `${value.substring(0, maxLength - 1)}${ellipsisString}`
  return value
}

interface NeedStringToBeClampedProps {
  value: string
  maxLength?: number
}
/**
 * If the value is not null, not empty, and the maxLength is greater than 0, and the value is longer
 * than the maxLength, then return true.
 * @param value  - It is a required string that we want to clamp (if necessary)
 * @param maxLength - It is optional and it is the max length that we will return. By default it is 18
 * @returns A boolean.
 */
export function needStringToBeClamped({ value, maxLength = 18 }: NeedStringToBeClampedProps): boolean {
  if (!value || !value.trim()) return false

  return maxLength < 1 || value.length > maxLength
}

export function makeID(length: number): string {
  let result = ""
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
  const charactersLength = characters.length
  let counter = 0
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

export function camelToSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

export function snakeCase(str: string): string {
  return str
    .replace(/\W+/g, " ")
    .split(/ |\B(?=[A-Z])/)
    .map((word: string) => word.toLowerCase())
    .join("_")
}

export function objectToFormData(obj: { [key: string]: unknown }): FormData {
  const formData = new FormData()

  Object.keys(obj).forEach((key) => {
    formData.append(key, z.coerce.string().parse(obj[key]))
  })
  return formData
}

export function upperFirst(str: string): string {
  return str
    .split(" ")
    .map((s) =>
      s
        .split("")
        .map((c, index) => (index === 0 ? c.toUpperCase() : c))
        .join("")
    )
    .join(" ")
}

export const ssr_upperFirst =
  // unstable_cache(
  async (str: string) => upperFirst(str)
//   ["ssr-upper-first"]
// )
